import { Copy } from 'lucide-react'
import { useQueryState } from 'nuqs'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import useSWR from 'swr'

import { PageHeader } from '@/components/ds'
import { Loading } from '@/components/Loading'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { useSetPageHeader } from '@/contexts/PageHeaderContext'
import { apiFetcher } from '@/data/fetcher'
import { ProductForm } from '@/forms/ProductForm/ProductForm'
import { Product } from '@/interfaces/catalog/Product'
import { getProductAxisIds, getProductParameterInputs } from '@/lib/catalog/productStructure'

export default function ProductNewPage() {
  const navigate = useNavigate()
  const { t } = useTranslation(undefined, { keyPrefix: 'catalog' })
  const [copyFrom] = useQueryState('copyFrom')
  const { data: source, isLoading } = useSWR<Product>(
    copyFrom && `/api/v2/products/${copyFrom}`,
    apiFetcher,
  )
  const title = t('products.new.title')

  useSetPageHeader({
    breadcrumbs: [
      { href: '/catalog', children: t('title') },
      { href: '/catalog/products', children: t('products.title') },
      { href: '/catalog/product/new', children: title },
    ],
  })

  if (isLoading) {
    return <Loading />
  }

  return (
    <>
      <title>{title}</title>
      <div className="mx-auto w-full max-w-[1280px]">
        <PageHeader title={title} description="Varianty, ceny a obrázky doplníte po uložení v detailu produktu." />

        {source && (
          <Alert variant="help" className="mb-6">
            <Copy />
            <AlertTitle>{t('products.copy.title', { name: source.name })}</AlertTitle>
            <AlertDescription>{t('products.copy.description')}</AlertDescription>
          </Alert>
        )}

        <ProductForm
          defaultValues={
            source && {
              name: t('products.copy.name', { name: source.name }),
              mainCategoryId: source.mainCategoryId ?? '',
              description: source.description || null,
              secondaryCategoryIds: source.secondaryCategoryIds || [],
              isActive: source.isActive ?? true,
              productType: (source.productType as 'physical' | 'virtual' | 'service' | 'package') || 'physical',
              variantAxisIds: getProductAxisIds(source),
              parameters: getProductParameterInputs(source),
            }
          }
          onCancel={() => navigate(-1)}
          onSuccess={(data) => {
            navigate(`/catalog/product/${data.id}`)
          }}
        />
      </div>
    </>
  )
}
